/**
 * Overzicht zoals in Stripe Dashboard (ingeschakeld / uitgeschakeld / in behandeling).
 * Bij wijzigingen in Stripe: deze lijst bijwerken en opnieuw deployen.
 */
export type StripePaymentMethodStatus = 'enabled' | 'disabled' | 'pending' | 'not_applicable';

export interface StripePaymentMethodRow {
  name: string;
  region: string;
  status: StripePaymentMethodStatus;
}

export interface StripePaymentMethodGroup {
  id: string;
  title: string;
  methods: StripePaymentMethodRow[];
}

export const STRIPE_PAYMENT_METHOD_GROUPS: StripePaymentMethodGroup[] = [
  {
    id: 'cards',
    title: 'Betaalkaarten',
    methods: [
      { name: 'Cartes Bancaires', region: 'Frankrijk', status: 'pending' },
      { name: 'Kaarten (Visa, Mastercard, …)', region: 'Alle regio’s', status: 'enabled' },
      { name: 'Koreaanse betaalkaarten', region: 'Zuid-Korea', status: 'disabled' },
    ],
  },
  {
    id: 'wallets',
    title: 'Digitale wallet',
    methods: [
      { name: 'Alipay', region: 'China', status: 'disabled' },
      { name: 'Amazon Pay', region: 'Alle regio’s', status: 'disabled' },
      { name: 'Apple Pay', region: 'Alle regio’s', status: 'enabled' },
      { name: 'Google Pay', region: 'Alle regio’s', status: 'enabled' },
      { name: 'Kakao Pay', region: 'Zuid-Korea', status: 'disabled' },
      { name: 'Link', region: 'Alle regio’s', status: 'enabled' },
      { name: 'MB WAY', region: 'Portugal', status: 'disabled' },
      { name: 'MobilePay', region: 'Denemarken en Finland', status: 'disabled' },
      { name: 'Naver Pay', region: 'Zuid-Korea', status: 'disabled' },
      { name: 'PAYCO', region: 'Zuid-Korea', status: 'disabled' },
      { name: 'PayPal', region: 'Alle regio’s', status: 'disabled' },
      { name: 'Revolut Pay', region: 'Europa en Verenigd Koninkrijk', status: 'disabled' },
      { name: 'Samsung Pay', region: 'Zuid-Korea', status: 'disabled' },
      { name: 'Satispay', region: 'Italië', status: 'disabled' },
      { name: 'WeChat Pay', region: 'China', status: 'disabled' },
    ],
  },
  {
    id: 'cash',
    title: 'Contante betaalmethode',
    methods: [{ name: 'Multibanco', region: 'Portugal', status: 'disabled' }],
  },
  {
    id: 'bank_redirect',
    title: 'Omleiding naar bank',
    methods: [
      { name: 'Bancontact', region: 'België', status: 'enabled' },
      { name: 'BLIK', region: 'Polen', status: 'enabled' },
      { name: 'EPS', region: 'Oostenrijk', status: 'enabled' },
      { name: 'iDEAL | Wero', region: 'Nederland', status: 'enabled' },
      { name: 'Przelewy24', region: 'Polen', status: 'disabled' },
      { name: 'TWINT', region: 'Zwitserland', status: 'disabled' },
    ],
  },
  {
    id: 'bnpl',
    title: 'Koop nu, betaal later',
    methods: [
      { name: 'Alma', region: 'Frankrijk', status: 'disabled' },
      { name: 'Billie', region: 'Europa', status: 'disabled' },
      { name: 'Klarna', region: 'Alle regio’s', status: 'enabled' },
    ],
  },
  {
    id: 'debit',
    title: 'Bankafschrijving',
    methods: [
      { name: 'ACH Direct Debit', region: 'Verenigde Staten', status: 'disabled' },
      { name: 'SEPA-incasso', region: 'Europa', status: 'not_applicable' },
    ],
  },
  {
    id: 'bank_transfer',
    title: 'Bankoverschrijvingen',
    methods: [
      {
        name: 'Bankoverschrijving',
        region: 'Europa, Verenigd Koninkrijk, Japan, Mexico en Verenigde Staten',
        status: 'disabled',
      },
    ],
  },
  {
    id: 'realtime',
    title: 'Realtime betalingen',
    methods: [
      { name: 'Pix', region: 'Brazilië', status: 'disabled' },
      { name: 'UPI', region: 'India', status: 'disabled' },
    ],
  },
];

export function stripePaymentMethodStats() {
  let enabled = 0;
  let disabled = 0;
  let pending = 0;
  let notApplicable = 0;
  const enabledNames: string[] = [];
  const pendingNames: string[] = [];
  for (const g of STRIPE_PAYMENT_METHOD_GROUPS) {
    for (const m of g.methods) {
      if (m.status === 'enabled') {
        enabled++;
        enabledNames.push(m.name);
      } else if (m.status === 'disabled') disabled++;
      else if (m.status === 'pending') {
        pending++;
        pendingNames.push(m.name);
      } else notApplicable++;
    }
  }
  return { enabled, disabled, pending, notApplicable, enabledNames, pendingNames };
}

/** Icon-slugs voor merkbadges in de UI (footer, prijzen, /betaalmethoden). */
export type PaymentBrandIconId =
  | 'visa'
  | 'mastercard'
  | 'ideal'
  | 'bancontact'
  | 'apple_pay'
  | 'google_pay'
  | 'klarna'
  | 'stripe_link'
  | 'blik'
  | 'eps';

export type CustomerPaymentRow = {
  /** Unieke sleutel voor React */
  key: string;
  /** Korte, vriendelijke naam */
  customerLabel: string;
  /** Eén regel uitleg voor klanten */
  customerHint: string;
  icons: PaymentBrandIconId[];
  sortOrder: number;
};

/**
 * Alleen ingeschakelde Stripe-methoden, met vriendelijke labels en iconen.
 * Bij nieuwe ingeschakelde methoden: hier meta toevoegen.
 */
const CUSTOMER_ROW_META: Record<string, Omit<CustomerPaymentRow, 'key'>> = {
  'iDEAL | Wero': {
    customerLabel: 'iDEAL en Wero',
    customerHint: 'Direct betalen vanaf je Nederlandse bankrekening.',
    icons: ['ideal'],
    sortOrder: 10,
  },
  Bancontact: {
    customerLabel: 'Bancontact',
    customerHint: 'Veilig betalen via je Belgische bank.',
    icons: ['bancontact'],
    sortOrder: 20,
  },
  'Kaarten (Visa, Mastercard, …)': {
    customerLabel: 'Betaalkaart',
    customerHint: 'Visa, Mastercard en andere gangbare kaarten.',
    icons: ['visa', 'mastercard'],
    sortOrder: 30,
  },
  'Apple Pay': {
    customerLabel: 'Apple Pay',
    customerHint: 'Snel afrekenen op iPhone, iPad en Mac.',
    icons: ['apple_pay'],
    sortOrder: 40,
  },
  'Google Pay': {
    customerLabel: 'Google Pay',
    customerHint: 'Betalen met je Google-account op telefoon of laptop.',
    icons: ['google_pay'],
    sortOrder: 50,
  },
  Klarna: {
    customerLabel: 'Klarna',
    customerHint: 'Betaalopties in termijnen of achteraf waar Klarna dat aanbiedt.',
    icons: ['klarna'],
    sortOrder: 60,
  },
  Link: {
    customerLabel: 'Link',
    customerHint: 'Stripe Link: opgeslagen betaalgegevens voor sneller afrekenen.',
    icons: ['stripe_link'],
    sortOrder: 70,
  },
  BLIK: {
    customerLabel: 'BLIK',
    customerHint: 'Populaire betaalmethode in Polen.',
    icons: ['blik'],
    sortOrder: 80,
  },
  EPS: {
    customerLabel: 'EPS',
    customerHint: 'Online bankbetaling voor klanten in Oostenrijk.',
    icons: ['eps'],
    sortOrder: 90,
  },
};

export function getCustomerFacingPayments(): CustomerPaymentRow[] {
  const out: CustomerPaymentRow[] = [];
  for (const g of STRIPE_PAYMENT_METHOD_GROUPS) {
    for (const m of g.methods) {
      if (m.status !== 'enabled') continue;
      const meta = CUSTOMER_ROW_META[m.name];
      if (!meta) continue;
      const slug = m.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      out.push({
        key: slug,
        customerLabel: meta.customerLabel,
        customerHint: meta.customerHint,
        icons: meta.icons,
        sortOrder: meta.sortOrder,
      });
    }
  }
  out.sort((a, b) => a.sortOrder - b.sortOrder);
  return out;
}

/** Platte lijst icon-id’s voor footer (uniek, volgorde zoals klantweergave). */
export function getFooterPaymentIconIds(): PaymentBrandIconId[] {
  const seen = new Set<PaymentBrandIconId>();
  const list: PaymentBrandIconId[] = [];
  for (const row of getCustomerFacingPayments()) {
    for (const id of row.icons) {
      if (!seen.has(id)) {
        seen.add(id);
        list.push(id);
      }
    }
  }
  return list;
}
